import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { sounds } from './audio/sfx.ts';
import { RulePrimer } from './components/RulePrimer.tsx';
import { SettingsPanel } from './components/SettingsPanel.tsx';
import { useForceLandscape } from './hooks/useForceLandscape.ts';
import {
  playerPreferences,
  preferencesPatch,
  useSettings,
  type Settings,
} from './hooks/useSettings.ts';
import { useI18n, type MessageKey } from './i18n/index.tsx';
import { GameClient, socketUrl } from './net/client.ts';
import { Auth } from './screens/Auth.tsx';
import { InviteLanding } from './screens/InviteLanding.tsx';
import { RulesPage } from './screens/RulesPage.tsx';
import { Lobby } from './screens/Lobby.tsx';
import { RoomScreen } from './screens/RoomScreen.tsx';
import { SessionComplete } from './screens/SessionComplete.tsx';
import { TableScreen } from './screens/TableScreen.tsx';

const ONBOARDED_KEY = 'rummy.onboarded';

interface InstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

function roomFromLocation(): string | null {
  const match = window.location.pathname.match(/^\/r\/([\w-]+)$/);
  return match?.[1] ?? null;
}

/** `/rules` is a real route, not a modal, so it can be sent to someone. */
function rulesFromLocation(): boolean {
  return window.location.pathname === '/rules';
}

export function App({ client }: { client: GameClient }) {
  const state = useSyncExternalStore(client.subscribe, client.getSnapshot);
  const { t } = useI18n();
  const [settings, updateSettings] = useSettings();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [rulesPage, setRulesPage] = useState(rulesFromLocation);
  const [rulesStep, setRulesStep] = useState(0);
  const [installPrompt, setInstallPrompt] = useState<InstallPromptEvent | null>(null);
  const [onboarded, setOnboarded] = useState(
    () => localStorage.getItem(ONBOARDED_KEY) === '1',
  );

  // Account preferences define the visual identity across devices. Controls
  // such as sound and reduced motion remain intentionally device-local.
  const appliedPreferences = useRef<string | null>(null);
  useEffect(() => {
    const preferences = state.identity?.preferences;
    if (!preferences) return;
    const fingerprint = JSON.stringify(preferences);
    if (appliedPreferences.current === fingerprint) return;
    appliedPreferences.current = fingerprint;
    updateSettings(preferencesPatch(preferences));
  }, [state.identity?.preferences, updateSettings]);

  const updateSettingsSynced = useCallback(
    (patch: Partial<Settings>) => {
      updateSettings(patch);
      const synced = playerPreferences({ ...settings, ...patch });
      if (JSON.stringify(synced) !== JSON.stringify(state.identity?.preferences)) {
        client.setPreferences(synced);
      }
    },
    [client, settings, state.identity?.preferences, updateSettings],
  );

  useEffect(() => {
    const capture = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as InstallPromptEvent);
    };
    const installed = () => setInstallPrompt(null);
    window.addEventListener('beforeinstallprompt', capture);
    window.addEventListener('appinstalled', installed);
    return () => {
      window.removeEventListener('beforeinstallprompt', capture);
      window.removeEventListener('appinstalled', installed);
    };
  }, []);

  const install = useCallback(async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    if (choice.outcome === 'accepted') setInstallPrompt(null);
  }, [installPrompt]);

  /**
   * The invite link *is* the mechanism (§4.3): opening it joins the room.
   *
   * It joins each link once. Without that, leaving a table is impossible: the
   * room clears, this effect re-reads a URL that still says `/r/<id>`, and puts
   * the player straight back in the seat they just stood up from.
   */
  const previewedFrom = useRef<string | null>(null);
  useEffect(() => {
    const target = roomFromLocation();
    if (!target || !onboarded || !state.identity) return;
    if (state.room?.id === target || state.preview?.id === target) return;
    if (previewedFrom.current === target) return;
    previewedFrom.current = target;
    client.previewRoom(target);
  }, [client, onboarded, state.identity, state.room?.id, state.preview?.id]);

  const leaveRoom = useCallback(() => {
    // Clear the deep link before asking to leave, so the effect above sees a
    // bare path rather than the room we are walking out of.
    if (window.location.pathname !== '/') {
      window.history.replaceState(null, '', '/');
    }
    previewedFrom.current = null;
    client.dismissPreview();
    client.leaveRoom();
  }, [client]);

  useEffect(() => {
    // An invite URL must survive authentication. Previously this effect saw
    // `room === null` during sign-up and immediately rewrote `/r/<code>` to
    // `/`, so the join effect had no destination left once identity arrived.
    if (!state.room && roomFromLocation()) return;
    // `/rules` is a destination in its own right; leave it alone until the
    // reader navigates away, or this effect would bounce them to the lobby.
    if (rulesPage) return;
    const path = state.room ? `/r/${state.room.id}` : '/';
    if (window.location.pathname !== path) {
      window.history.replaceState(null, '', path);
    }
  }, [state.room, rulesPage]);

  // Back/forward between the rules and wherever the reader came from.
  useEffect(() => {
    const onPop = () => setRulesPage(rulesFromLocation());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const openRules = useCallback(() => {
    window.history.pushState(null, '', '/rules');
    setRulesPage(true);
  }, []);

  const closeRules = useCallback(() => {
    setRulesPage(false);
    if (rulesFromLocation()) window.history.replaceState(null, '', '/');
  }, []);

  // A bad/expired invite should not leave the user on a route that we have
  // already established cannot resolve. Valid invites stay intact throughout
  // the auth hand-off above.
  useEffect(() => {
    if (state.error?.code !== 'no-room' || state.room !== null || !roomFromLocation()) return;
    window.history.replaceState(null, '', '/');
    previewedFrom.current = null;
    client.dismissPreview();
  }, [state.error?.code, state.room]);

  const onAuth = useCallback(
    (handle: string, avatar: string) => {
      client.setProfile(handle, avatar);
      localStorage.setItem(ONBOARDED_KEY, '1');
      setOnboarded(true);
    },
    [client],
  );

  /** Credentials accepted over HTTP; the socket re-identifies with the new
   *  token. An account that already has a face and a name skips the profile
   *  step entirely. */
  const onSignedIn = useCallback(
    (fresh: boolean) => {
      client.reidentify();
      if (!fresh) {
        localStorage.setItem(ONBOARDED_KEY, '1');
        setOnboarded(true);
      }
    },
    [client],
  );

  const onSignOut = useCallback(() => {
    localStorage.removeItem('rummy.token');
    localStorage.removeItem('rummy.profile');
    localStorage.removeItem(ONBOARDED_KEY);
    window.location.href = '/';
  }, []);

  const onAuthScreen = !onboarded || state.identity === null;
  /*
   * The board is the one screen that must fit the viewport exactly. Everything
   * else (lobby, room, rules, summary) is a document and may scroll; the table
   * is a fixed playing surface, and a table you have to scroll is a table where
   * the Discard button is somewhere you cannot see it.
   */
  const atTable =
    !onAuthScreen &&
    !rulesPage &&
    state.room !== null &&
    (state.room.state === 'in_hand' || state.room.state === 'hand_summary');

  // Every screen once you are in a room — waiting room, hand in progress, win
  // celebration — is "the game" and gets the landscape board. The Lobby and
  // Auth screens are outside a room and stay portrait-native.
  useForceLandscape(state.room !== null);

  /**
   * A strip that flashes up for 200ms on every page load is noise. Give the
   * first connection a moment to succeed quietly before saying anything.
   */
  const [connectionSettled, setConnectionSettled] = useState(false);
  useEffect(() => {
    if (state.connection === 'open') {
      setConnectionSettled(true);
      return;
    }
    const id = setTimeout(() => setConnectionSettled(true), 900);
    return () => clearTimeout(id);
  }, [state.connection]);
  // The auth card states the transport problem itself, next to the button it
  // affects and next to the retry. A strip saying the same sentence at the top
  // of the same screen is not twice as informative.
  const showConnectionStrip =
    state.connection !== 'open' && connectionSettled && !onAuthScreen;

  const errorText = useMemo(() => {
    if (!state.error) return null;
    const key = `error.${state.error.code}` as MessageKey;
    return t(key) || t('error.generic');
  }, [state.error, t]);

  useEffect(() => {
    if (!state.error) return;
    const id = setTimeout(() => client.clearError(), 3200);
    return () => clearTimeout(id);
  }, [state.error, client]);

  const screen = (() => {
    // Ahead of the auth gate on purpose: the rules are how somebody decides
    // whether to sign up at all, and the link gets forwarded to people who
    // have no account yet. A rules page you must log in to read is not one.
    if (rulesPage) return <RulesPage onBack={closeRules} />;
    // Spelt out rather than reusing `onAuthScreen`, because this is also what
    // narrows `state.identity` to non-null for every branch below it.
    if (!onboarded || !state.identity) {
      return (
        <Auth
          initialHandle={state.identity?.handle ?? ''}
          initialAvatar={state.identity?.avatar ?? ''}
          onSubmit={onAuth}
          onSignedIn={onSignedIn}
          connection={state.connection}
          pending={onboarded && state.identity === null}
          onRetry={() => client.retry()}
        />
      );
    }
    const { room, table } = state;
    if (!room && state.preview && roomFromLocation() === state.preview.id) {
      return (
        <InviteLanding
          preview={state.preview}
          onJoin={() => client.joinRoom(state.preview!.id)}
          onWatch={() => client.spectateRoom(state.preview!.id)}
          onCancel={() => {
            window.history.replaceState(null, '', '/');
            previewedFrom.current = null;
            client.dismissPreview();
          }}
          onRules={openRules}
        />
      );
    }
    if (!room) {
      return (
        <Lobby
          identity={state.identity}
          friends={state.friends}
          rooms={state.rooms}
          onCreate={() => client.createRoom()}
          onPractice={(seats, difficulty) => {
            // One tap to a full practice table: the messages are processed in
            // order on this connection, so the seats exist before the bots ask
            // to sit in them. `fill-seats` does the loop server-side, so a
            // six-seat table is one message rather than five.
            client.createRoom({ seats, fillSkill: difficulty });
            client.fillSeats(difficulty);
          }}
          onJoin={(id) => client.joinRoom(id)}
          onWatch={(id) => client.spectateRoom(id)}
          onShowRules={() => {
            setRulesStep(0);
            setRulesOpen(true);
          }}
        />
      );
    }
    if (room.state === 'session_complete' && table) {
      return (
        <SessionComplete
          table={table}
          room={room}
          client={client}
          accountId={state.identity.id}
          onLeave={leaveRoom}
          sound={settings.sound}
          careerStats={state.identity.stats}
        />
      );
    }
    if ((room.state === 'in_hand' || room.state === 'hand_summary') && table) {
      return (
        <TableScreen
          table={table}
          room={room}
          client={client}
          accountId={state.identity.id}
          confirmDiscard={settings.confirmDiscard}
          quickPlay={settings.quickPlay}
          expertMode={settings.expertMode}
          showTableIntel={settings.showTableIntel}
          showGameplayGuidance={settings.showGameplayGuidance}
          sound={settings.sound}
          turnAlert={settings.turnAlert}
          turnNotifications={settings.turnNotifications}
          cardSize={settings.cardSize}
          connection={state.connection}
          disconnectedAt={state.disconnectedAt}
          pendingDraw={state.pendingDraw}
          pendingAction={state.pendingAction}
          onLeave={leaveRoom}
          onOpenSettings={() => setSettingsOpen(true)}
        />
      );
    }
    return (
      <RoomScreen
        room={room}
        friends={state.friends}
        accountId={state.identity.id}
        client={client}
        onLeave={leaveRoom}
      />
    );
  })();

  // Browsers will not start an AudioContext outside a user gesture, so the
  // first click or key press is what actually enables sound.
  useEffect(() => {
    sounds.setEnabled(settings.sound);
    sounds.setAlertsEnabled(settings.turnAlert);
    if (!settings.sound && !settings.turnAlert) return;
    const unlock = () => sounds.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, [settings.sound, settings.turnAlert]);

  return (
    <div className="app" data-board={atTable ? 'true' : 'false'}>
      <div className="app__chrome">
        <span className="app__brand">
          <img className="app__logo" src="/logo.svg" alt="" />
          <span className="app__mark">{t('app.name')}</span>
        </span>
        <span className="spacer" />
        {installPrompt && (
          <button type="button" className="btn btn--small btn--install" onClick={() => void install()}>
            {t('app.install')}
          </button>
        )}
        <button
          type="button"
          className="btn btn--ghost btn--small"
          onClick={() => setSettingsOpen(true)}
        >
          {t('settings.heading')}
        </button>
      </div>

      {screen}

      {/* §8.1 — transport loss is a normal path, so it gets a calm strip rather
          than a modal that stops the world. Opening the socket for the first
          time is not a loss and does not get the alarming copy. */}
      {showConnectionStrip && (
        <div className={`connection connection--${state.connection}`} role="status">
          {t(
            state.connection === 'connecting'
              ? 'error.connecting'
              : state.connection === 'offline'
                ? 'error.offline'
                : 'error.connection',
          )}
        </div>
      )}

      {errorText && (
        <div className="toast" role="alert">
          {errorText}
        </div>
      )}

      {state.invite && (
        <div className="toast toast--invite" role="alert">
          <span>{state.invite.from} · {state.invite.roomLabel}</span>
          <button
            type="button"
            className="btn btn--small btn--primary"
            onClick={() => {
              client.joinRoom(state.invite!.roomId);
              client.dismissInvite();
            }}
          >
            {t('lobby.join')}
          </button>
          <button
            type="button"
            className="btn btn--small btn--ghost"
            onClick={() => client.dismissInvite()}
          >
            {t('settings.close')}
          </button>
        </div>
      )}

      {settingsOpen && (
        <SettingsPanel
          settings={settings}
          onChange={updateSettingsSynced}
          onClose={() => setSettingsOpen(false)}
          identity={state.identity}
          onSignOut={onboarded ? onSignOut : undefined}
        />
      )}

      {rulesOpen && (
        <RulePrimer
          step={rulesStep}
          onStep={setRulesStep}
          onClose={() => setRulesOpen(false)}
          onFullRules={() => {
            setRulesOpen(false);
            openRules();
          }}
        />
      )}
    </div>
  );
}

export function createClient(): GameClient {
  return new GameClient(socketUrl());
}
